/**
 * נתוני דוח ביקור **סינתטיים** — לבדיקות ולתרחישי ה־PDF של שלב 6 (IMPLEMENTATION-PLAN
 * §6.3). אין כאן שום נתון אמיתי; כל השמות והאתרים מסומנים כהדגמה (C-09).
 */

import { CHECKLIST_DRAFT_0_1 } from './checklistContent.ts';
import { CHECKLIST_DRAFT_0_2 } from './checklistContent02.ts';
import { CHECKLIST_1_0 } from './checklistContent10.ts';
import type { FollowupPhoto, FollowupState } from './followup.ts';
import { sampleCorrectionsData } from './followupReport.ts';
import type { SiteFacts, StageKind } from './siteProfile.ts';
import {
  decideApplicability,
  ITEM_REMOVED_REASON,
  type AnswerValue,
  type NotCheckedReason,
  type StoredSeverity,
} from './visitEngine.ts';
import type { FollowupReportItem, ReportEnvelope, VisitReportData } from './visitReport.ts';

/**
 * תרחישים (IMPLEMENTATION-PLAN §6.3 + D24):
 * - `baseline` — גם ״snapshot ישן״: בלי `visit_details` (לפני D22) ועם `normal` בממצא קודם.
 * - `item-photos` — סעיף עם 1 / 3 / 7 תמונות (D39: עד 2 בשורה, ״+N בנספח״, ובנספח רק העודפות; עד
 *   D39 — 2 ממוזערות בשורה וכולן שוב בנספח, A-111).
 * - `finding-photos` — ממצא כללי עם 4 תמונות (D39: 2 בשורה שלו ב״ממצאים כלליים״, 2 בנספח; עד D39 —
 *   בכרטיס הממצא).
 * - `defect-without-finding` — ליקוי עם הערה ותמונה, בלי ממצא ממוספר (D23).
 * - `cover-no-parties` — שער בלי מזמין, קבלן וכתובת (שדה ריק אינו מודפס).
 * - `removed-chapter` / `removed-item` / `immediate-on-removed` — D21.
 * - `long-notes` — הערות הבקר 3,000 תווים + פרטי ביקור (D22).
 * - `unknown-severity` — ערך חומרה לא מוכר ב־snapshot ישן (״לא צוינה״).
 * - `full-checklist-0-2` — צ׳קליסט מלא של טיוטה 0.2 (20 פרקים, 141 סעיפים; עמודים רבים):
 *   פרק 11 סונן לפי שלב (כלל מוצר), C2-05 סונן לפי הפרופיל — כותרות הטבלאות ושמות
 *   הפרקים נבדקים בכל העמודים.
 * - `full-checklist-1-0` — צ׳קליסט מלא של תוכן 1.0 (143 סעיפים; D37, D38), נתונים בלבד — ההדפסה
 *   החדשה היא פרומפט י״ד: שלב ׳שלד׳ — 11, 12, 19 סוננו לפי שלב, 10, 13, 14 בביקור; חמשת
 *   הפרקים ״לבחירה״ לא נבחרו; התבנית אינה הדגמה ומאושרת (approved_by_name, approved_on); schema 2.
 * - מעקב (D25, פרק 9): `baseline` — שני ממצאים קודמים בלי רשומה (״לא נבדקו 2״);
 *   `followup-pending` — אחד ״דווח כתוקן, טרם אומת״ עם לפני/אחרי (לא ״נסגרו״);
 *   `followup-all-verified` — כולם ״תוקן ואומת״; `followup-mixed` — 2 אומתו · 1 לא תוקן ·
 *   1 דווח · 1 לא נבדק; `followup-fifty` — 50 ממצאים קודמים; `followup-no-after` — ״תוקן״
 *   בלי תמונת ״אחרי״; `followup-after-unknown-time` — תמונת ״אחרי״ בלי מועד (RQ-G06).
 *   **כל אלה — snapshot מלפני D28 (schema 1), כולל פרק 9 הישן; הם נשארים כדי לוודא שדוח שכבר
 *   נסקר או אושר מוצג כמו שהוא.**
 * - D28 (schema 2): `visit-not-attached` — דוח ביקור בלי פרק התיקונים (ברירת המחדל);
 *   `visit-attached` — הבקר צירף את התיקונים: ממצא, סעיף בלי ממצא וסעיף בלי רשומה.
 * - D39 (דוח בסגנון טבלה; schema 2, תוכן 0.1): `row-photos` — שורות עם 0, 1, 2 ו־5 תמונות,
 *   ממצא ותמונות סעיף באותה שורה (תמונת הממצא קודם), ממצא כללי עם 5 תמונות ותמונה כללית —
 *   שתיים בשורה, השאר בנספח (39-2); `defects-in-rows` — הליקוי בשורת הסעיף: כל החומרות (וגם
 *   ״לא צוינה״), שני ממצאים בשורה אחת (החומרה הגבוהה בצ׳יפ), התראה על ממצא בשורה ועל ממצא כללי,
 *   תיאור ארוך, ״דורש תיקון״ עם ממצא, ״לא תקין״ בלי ממצא (D23) ושני ממצאים כלליים.
 * - D39 על תוכן 1.0 (schema 2): `table-1-0` — הדוח בסגנון טבלה כמו בדוגמה: תוכן שאושר (״אושר
 *   לשימוש בידי״, בלי תוויות אימות) באתר שאינו הדגמה; שלבי שלד וגמר — 5 פרקים ״לבחירה״ ו־2 ״לפי
 *   שלב״ לא נכללו; ממצאים ותמונות רק בפרקי ״תמיד״; `excluded-many` — 11 פרקים שלא נכללו (שלב ׳אחר׳
 *   שאינו מתאים לאף פרק ״לפי שלב״, וחמשת הפרקים ״לבחירה״).
 * - `legacy-0-2` — snapshot ישן על טיוטת 0.2 (הדגמה, schema 2): הערת ההדגמה, ״מצב אימות הסעיפים״
 *   ו״נוסח אומת״ בשורות — כמו היום; פרקים שסוננו לפי שלב ופרק שהוסר — בשורת ״לא נכללו״ אחת.
 */
export type VisitReportScenario =
  | 'baseline'
  | 'no-findings'
  | 'long-finding'
  | 'fifty-findings'
  | 'hundred-photos'
  | 'no-plan'
  | 'injection'
  | 'item-photos'
  | 'finding-photos'
  | 'defect-without-finding'
  | 'cover-no-parties'
  | 'removed-chapter'
  | 'removed-item'
  | 'immediate-on-removed'
  | 'long-notes'
  | 'unknown-severity'
  | 'full-checklist-0-2'
  | 'full-checklist-1-0'
  | 'followup-pending'
  | 'followup-all-verified'
  | 'followup-mixed'
  | 'followup-fifty'
  | 'followup-no-after'
  | 'followup-after-unknown-time'
  | 'visit-not-attached'
  | 'visit-attached'
  | 'row-photos'
  | 'defects-in-rows'
  | 'table-1-0'
  | 'excluded-many'
  | 'legacy-0-2';

export const VISIT_REPORT_SCENARIOS: VisitReportScenario[] = [
  'baseline',
  'no-findings',
  'long-finding',
  'fifty-findings',
  'hundred-photos',
  'no-plan',
  'injection',
  'item-photos',
  'finding-photos',
  'defect-without-finding',
  'cover-no-parties',
  'removed-chapter',
  'removed-item',
  'immediate-on-removed',
  'long-notes',
  'unknown-severity',
  'full-checklist-0-2',
  'full-checklist-1-0',
  'followup-pending',
  'followup-all-verified',
  'followup-mixed',
  'followup-fifty',
  'followup-no-after',
  'followup-after-unknown-time',
  'visit-not-attached',
  'visit-attached',
  'row-photos',
  'defects-in-rows',
  'table-1-0',
  'excluded-many',
  'legacy-0-2',
];

const U = {
  a: '11111111-1111-4111-8111-111111111111',
  b: '22222222-2222-4222-8222-222222222222',
};

function uuid(prefix: string, n: number): string {
  const hex = n.toString(16).padStart(12, '0');
  return `${prefix.padEnd(8, '0').slice(0, 8)}-0000-4000-8000-${hex}`;
}

const LONG_TEXT = Array.from(
  { length: 9 },
  (_, i) =>
    `פסקה ${i + 1}: בצד המזרחי של מגדל A, בקומה 7, נמצא משטח עבודה ללא מעקה תקני לאורך כ־12 מ׳. ` +
    'העובדים ביצעו יציקה בקרבת השפה בלי רתמה מחוברת. לפי תקנה 2א(ב)(3) — ראו גם ת״י 1139 — ' +
    'נדרש מעקה בגובה 1.00 מ׳ לפחות עם לוח רגל. הבקר עצר את העבודה באזור (Stop Work) עד להתקנה, ' +
    'והודיע למנהל העבודה בטלפון 052-1234567 בשעה 10:45. יש לבדוק שוב את כל שולי הקומה.',
).join('\n\n');

export interface SampleOptions {
  /**
   * שורה ארוכה בתחתית העמוד (RQ-I04; עד D39 — כרטיס ממצא, תקלת 01.10.2026 — ״המשך״ ריק): ממצא 7
   * בלי תמונה, עם תיאור של N שורות, בשורת הסעיף שלו. הערך שמביא את השורה לגבול העמוד תלוי
   * בפריסה — לכן תרחישי ה־PDF סורקים טווח (stage6-samples.ts).
   */
  tailLines?: number;
}

/** התרחישים על תוכן 1.0 (D37) ועל טיוטת 0.2; כל השאר — טיוטת 0.1. */
const CONTENT_1_0: readonly VisitReportScenario[] = [
  'full-checklist-1-0',
  'table-1-0',
  'excluded-many',
];
const CONTENT_0_2: readonly VisitReportScenario[] = ['full-checklist-0-2', 'legacy-0-2'];

/**
 * ביקור הדגמה עם הצ׳קליסט של טיוטת התוכן 0.1 (או 0.2 בתרחישים `full-checklist-0-2` ו־`legacy-0-2`,
 * ו־1.0 בתרחישים `full-checklist-1-0`, `table-1-0` ו־`excluded-many`).
 */
export function sampleVisitReportData(
  scenario: VisitReportScenario = 'baseline',
  options: SampleOptions = {},
): VisitReportData {
  const content = CONTENT_1_0.includes(scenario)
    ? CHECKLIST_1_0
    : CONTENT_0_2.includes(scenario)
      ? CHECKLIST_DRAFT_0_2
      : CHECKLIST_DRAFT_0_1;
  const chapters = content.chapters.map((c, i) => ({
    id: uuid('c', i + 1),
    code: c.code,
    title: c.title,
    answer_scheme: c.scheme,
  }));
  let qn = 0;
  const questions = content.chapters.flatMap((c, ci) =>
    c.questions.map((q) => ({
      id: uuid('q', ++qn),
      chapter_id: chapters[ci]!.id,
      code: q.code,
      title: q.title,
      source_text: q.sourceText,
      verification_status: q.verification,
    })),
  );
  const byCode = new Map(questions.map((q) => [q.code, q]));
  const qid = (code: string) => byCode.get(code)!.id;

  // סינון אחד לדוגמה (C8-01) — מוצג בדוח עם הסיבה.
  const items = questions.map((q) => ({
    question_id: q.id,
    applicable: q.code !== 'C8-01',
    reason: q.code === 'C8-01' ? 'לא מתקיים: אין עבודות הריסה באתר (הדגמה)' : 'חל בכל ביקור',
  }));

  const t = (m: number) => new Date(Date.UTC(2026, 8, 28, 6, m)).toISOString();
  const answers: VisitReportData['answers'] = [
    {
      question_id: qid('C1-01'),
      state: 'ok',
      note: '',
      not_checked_reason: null,
      answered_by: U.a,
      answered_at: t(5),
    },
    {
      question_id: qid('C4-01'),
      state: 'ok',
      note: 'גדר היקפית תקינה, שער נעול.',
      not_checked_reason: null,
      answered_by: U.a,
      answered_at: t(8),
    },
    {
      question_id: qid('C4-02'),
      state: 'not_ok',
      note: 'שילוט חסר בכניסה הצפונית.',
      not_checked_reason: null,
      answered_by: U.b,
      answered_at: t(12),
    },
    {
      question_id: qid('C2-01'),
      state: 'not_relevant',
      note: 'אין עבודה בגובה ביום הביקור.',
      not_checked_reason: null,
      answered_by: U.a,
      answered_at: t(14),
    },
    {
      question_id: qid('C3-01'),
      state: 'not_relevant',
      note: '',
      not_checked_reason: null,
      answered_by: U.b,
      answered_at: t(15),
    },
    {
      question_id: qid('C5-01'),
      state: null,
      note: 'הקומה נעולה.',
      not_checked_reason: 'area_inaccessible',
      answered_by: U.a,
      answered_at: t(18),
    },
    {
      question_id: qid('C6-01'),
      state: null,
      note: '',
      not_checked_reason: 'work_not_done',
      answered_by: U.a,
      answered_at: t(19),
    },
  ];

  const findings: VisitReportData['findings'] = [];
  const evidence: VisitReportData['evidence'] = [];
  const addPhoto = (
    n: number,
    findingId: string | null,
    questionId: string | null,
    caption: string,
  ) =>
    evidence.push({
      id: uuid('e', n),
      question_id: questionId,
      finding_id: findingId,
      storage_path: `site/${uuid('e', n)}/photo-${n}.jpg`,
      mime_type: 'image/jpeg',
      sha256: 'a'.repeat(64),
      size_bytes: 250_000,
      caption,
      observed_at: n % 3 === 0 ? null : t(20 + (n % 30)),
      observed_at_source: n % 3 === 0 ? 'unknown' : 'device',
      source: n % 2 === 0 ? 'camera' : 'upload',
      uploaded_by: n % 2 === 0 ? U.a : U.b,
      uploaded_at: t(20 + (n % 30)),
      stored: true,
    });

  if (scenario !== 'no-findings') {
    findings.push({
      id: uuid('f', 1),
      question_id: qid('C4-02'),
      finding_number: 7,
      description: 'שלט אזהרה ומספרי חירום חסרים בכניסה הצפונית לאתר.',
      location_text: 'כניסה צפונית',
      area_name: 'מגדל A',
      severity: 'low',
      created_by: U.b,
      created_at: t(13),
      withdrawn_at: null,
      withdrawn_reason: null,
    });
    findings.push({
      id: uuid('f', 2),
      question_id: null,
      finding_number: 8,
      description:
        scenario === 'long-finding'
          ? LONG_TEXT
          : 'חפירה פתוחה בעומק 2.4 מ׳ ללא גידור ליד דרך הגישה (Gate 3). ממצא כללי שאינו בצ׳קליסט.',
      location_text: 'דרך גישה, שער 3',
      area_name: null,
      severity: 'immediate',
      created_by: U.a,
      created_at: t(16),
      withdrawn_at: null,
      withdrawn_reason: null,
    });
    findings.push({
      id: uuid('f', 3),
      question_id: null,
      finding_number: 9,
      description: 'נרשם בטעות — כפילות של ממצא 8.',
      location_text: '',
      area_name: null,
      severity: 'high',
      created_by: U.a,
      created_at: t(17),
      withdrawn_at: t(18),
      withdrawn_reason: 'כפילות',
    });
    addPhoto(1, uuid('f', 1), qid('C4-02'), 'הכניסה הצפונית — אין שלט');
    addPhoto(2, uuid('f', 2), null, 'חפירה פתוחה, מבט מדרום');
    addPhoto(3, uuid('f', 2), null, '');
  }
  addPhoto(4, null, qid('C4-01'), 'גדר היקפית — צד מערבי');

  if (scenario === 'fifty-findings') {
    for (let i = 0; i < 48; i += 1) {
      findings.push({
        id: uuid('f', 100 + i),
        question_id: i % 4 === 0 ? qid('C4-02') : null,
        finding_number: 20 + i,
        description: `ממצא עומס ${i + 1}: פיגום ללא לוח רגל בקומה ${(i % 12) + 1}, קטע ${i + 3}.`,
        location_text: `קומה ${(i % 12) + 1}`,
        area_name: i % 2 === 0 ? 'מגדל A' : 'חניון B',
        severity: (['immediate', 'high', 'low'] as const)[i % 3]!,
        created_by: i % 2 === 0 ? U.a : U.b,
        created_at: t(20 + (i % 30)),
        withdrawn_at: null,
        withdrawn_reason: null,
      });
    }
  }
  if (scenario === 'hundred-photos') {
    for (let i = 0; i < 96; i += 1)
      addPhoto(10 + i, i < 60 ? uuid('f', 2) : null, null, `תמונת עומס ${i + 1}`);
  }

  if (options.tailLines !== undefined && findings.length > 0) {
    findings[0]!.description = Array.from(
      { length: options.tailLines },
      (_, i) => `שורה ${i + 1}: ליקוי שנצפה בקומה ${(i % 9) + 1} (הדגמה).`,
    ).join('\n');
    for (const e of evidence) if (e.finding_id === findings[0]!.id) e.finding_id = null;
  }

  let visitLimitations =
    'קומות 9–12 לא נבדקו (אין גישה). המרתף נבדק חלקית בגלל הצפה.\nנבדק רק צד מזרח של האתר.';
  let siteName = 'אתר הדגמה א — מגדל A';
  let alertDescription = 'חפירה פתוחה ליד דרך גישה — סיכון נפילה מיידי.';
  if (scenario === 'injection') {
    siteName = 'אתר <b>מודגש</b> & "ציטוט"';
    findings[0]!.description =
      '<script>window.injected=1</script><img src=x onerror="window.injected=2"> תיאור';
    answers[1]!.note = 'ת.ז. של מנהל העבודה 123456789 — לא אמור להופיע במלואו.';
    evidence[0]!.caption = '<svg onload=alert(1)>';
    visitLimitations = '</style><h1>not a heading</h1>';
    alertDescription = '"><iframe src="https://example.invalid"></iframe>';
  }

  const data: VisitReportData = {
    schema: 1,
    organization: { name: 'חכשורי בטיחות' },
    site: { id: uuid('s', 1), name: siteName, address: 'רחוב הדגמה 12, עיר הדגמה', is_demo: true },
    profile: {
      permit_area_sqm: 18400,
      peak_worker_count: 65,
      safety_plan_kind: 'management',
      safety_plan_prepared_by: 'ממונה בטיחות (הדגמה)',
      safety_plan_updated_on: '2026-08-01',
      requires_section_192_notice: true,
      developer_name: 'מזמין הדגמה בע״מ',
      main_contractor_name: 'קבלן הדגמה (Demo Builders)',
      notes: null,
    },
    appointments: [
      {
        role: 'work_manager',
        role_other_label: null,
        person_name: 'מנהל עבודה (הדגמה)',
        starts_on: '2026-03-01',
        ends_on: null,
      },
      {
        role: 'safety_inspector',
        role_other_label: null,
        person_name: 'בקר בטיחות (הדגמה)',
        starts_on: null,
        ends_on: null,
      },
    ],
    visit: {
      id: uuid('v', 1),
      visit_date: '2026-09-28',
      opened_by: U.a,
      opened_at: t(0),
      submitted_by: U.a,
      submitted_at: t(55),
      plan_missing_reason: scenario === 'no-plan' ? 'התוכנית לא הוצגה באתר ולא נמסרה לבקר' : null,
      limitations: visitLimitations,
    },
    // D37 ס׳ 3: בגרסה שאינה הדגמה — גם מי פרסם ומתי, כמו private.visit_report_data; גרסת
    // הדגמה (0.1, 0.2) — בדיוק כמו קודם.
    template: content.isDemo
      ? {
          title: 'צ׳קליסט ביקור — טיוטת תוכן',
          version_label: content.versionLabel,
          is_demo: true,
        }
      : {
          title: content.title,
          version_label: content.versionLabel,
          is_demo: false,
          approved_by_name: 'מנהלת הדגמה',
          approved_on: '2026-10-20',
        },
    chapters,
    questions,
    items,
    answers,
    findings,
    evidence,
    alerts:
      scenario === 'no-findings'
        ? []
        : [
            {
              id: uuid('a', 1),
              finding_id: uuid('f', 2),
              description: alertDescription,
              location_text: 'שער 3',
              created_by: U.a,
              created_at: t(16),
            },
          ],
    areas: [
      { area_id: uuid('ar', 1), name: 'מגדל A' },
      { area_id: uuid('ar', 2), name: 'חניון B' },
    ],
    stages: [
      { stage_period_id: uuid('sp', 1), area_id: uuid('ar', 1), name: 'שלד' },
      { stage_period_id: uuid('sp', 2), area_id: uuid('ar', 1), name: 'חפירה ודיפון' },
      { stage_period_id: uuid('sp', 3), area_id: uuid('ar', 2), name: 'גמר' },
    ],
    plan:
      scenario === 'no-plan'
        ? null
        : {
            document_title: 'תוכנית ניהול בטיחות — הדגמה',
            version_number: 3,
            file_name: 'safety-plan-v3.pdf',
            revision_label: 'מהדורה 2א',
            revision_date: '2026-08-01',
            uploaded_at: '2026-08-02T09:30:00.000Z',
            uploaded_by_name: 'בקר א (הדגמה)',
            page_count: 42,
            has_text_layer: false,
            sha256: 'b'.repeat(64),
            selected: true,
            selected_at: '2026-08-02T09:31:00.000Z',
            approval_shown_at: null,
            approval_note: null,
          },
    previous_findings:
      scenario === 'no-findings'
        ? []
        : [
            {
              id: uuid('pf', 1),
              finding_number: 3,
              description: 'חסר מעקה בקומה 4',
              location_text: 'קומה 4',
              severity: 'high',
              visit_date: '2026-09-14',
            },
            {
              id: uuid('pf', 2),
              finding_number: 5,
              description: 'כבל חשמל על הקרקע במעבר',
              location_text: '',
              // ערך שהוקפא לפני D19 — מוצג ״נמוך״ (normalizeSeverity), ה־snapshot אינו משתנה.
              severity: 'normal',
              visit_date: '2026-09-21',
            },
          ],
    people: { [U.a]: 'בקר א (הדגמה)', [U.b]: 'בקר ב (הדגמה)' },
  };

  const remove = (codes: string[]) => {
    for (const i of data.items)
      if (codes.includes(byCode.get(questions.find((q) => q.id === i.question_id)!.code)!.code)) {
        i.applicable = false;
        i.reason = ITEM_REMOVED_REASON;
      }
  };
  const codeOf = (id: string) => questions.find((q) => q.id === id)!.code;
  // תוכן 1.0 — העובדות כמו בתרחיש של 0.2 ובפרופיל של הדוגמה: שטח מתחת לסף (C2-05 סונן), ביקור
  // ראשון (C5-06, C5-07 סוננו), האתר מחייב הודעה; מינוי מנהל אתר ועובדים זרים — ״נדרש בירור״; והשלבים
  // שנבחרו בפתיחת הביקור.
  const facts10 = (activeStageKinds: StageKind[]): SiteFacts => ({
    permitAreaAtLeastSiteManagerThreshold: 'no',
    hasActiveSiteManager: 'unknown',
    requiresSection192Notice: 'yes',
    safetyPlanKind: 'management',
    employsForeignWorkers: 'unknown',
    isFollowUpVisit: 'no',
    activeStageKinds,
  });
  // התחולה של כל סעיף — כפי שטופס הפתיחה קובע (decideApplicability; פרק ״לבחירה״ שלא סומן — לא בביקור).
  const decideItems = (facts: SiteFacts) => {
    const ruleOf = new Map(
      content.chapters.flatMap((c) => c.questions).map((q) => [qid(q.code), q.applicability]),
    );
    for (const i of data.items) {
      const decision = decideApplicability(ruleOf.get(i.question_id)!, facts);
      i.applicable = decision.applicable;
      i.reason = decision.reason;
    }
  };
  // פרק 4 של 1.0 (״לבחירה״) אינו בביקור, ולכן אין בו תשובות. מה שהדוגמה רושמת על C4-01 ו־C4-02 — גדר,
  // שילוט, ממצא 7 ותמונותיו — עובר לסעיפים שבהם הוא עוסק, בפרק 9 (״תמיד״).
  const moveChapter4 = () => {
    const moved = new Map([
      [qid('C4-01'), qid('C9-01')],
      [qid('C4-02'), qid('C9-02')],
    ]);
    const move = (id: string) => moved.get(id) ?? id;
    for (const a of answers) a.question_id = move(a.question_id);
    for (const f of findings) if (f.question_id !== null) f.question_id = move(f.question_id);
    for (const e of evidence) if (e.question_id !== null) e.question_id = move(e.question_id);
  };
  switch (scenario) {
    case 'item-photos':
      addPhoto(50, null, qid('C1-01'), 'תוכנית הבטיחות על הלוח');
      addPhoto(51, null, qid('C4-01'), 'גדר — צד צפוני');
      addPhoto(52, null, qid('C4-01'), '');
      for (let i = 0; i < 7; i += 1) addPhoto(60 + i, null, qid('C4-02'), `שילוט ${i + 1}`);
      break;
    case 'finding-photos':
      // ממצא 8 (כללי) עם 4 תמונות — D39: 2 בשורה שלו ב״ממצאים כלליים״, והיתר בנספח.
      addPhoto(70, uuid('f', 2), null, 'חפירה — מבט ממזרח');
      addPhoto(71, uuid('f', 2), null, 'חפירה — שלט חסר');
      break;
    case 'defect-without-finding':
      // ליקוי ב־C4-02 בלי ממצא: הממצא 7 ותמונתו הופכים לתמונת סעיף; ההערה והתמונה הם הליקוי.
      data.findings = findings.filter((f) => f.id !== uuid('f', 1));
      for (const e of evidence) if (e.finding_id === uuid('f', 1)) e.finding_id = null;
      break;
    case 'cover-no-parties':
      data.site.address = '';
      data.profile!.developer_name = null;
      data.profile!.main_contractor_name = null;
      data.appointments = [];
      break;
    case 'removed-chapter':
      remove(questions.filter((q) => q.code.startsWith('C6-')).map((q) => q.code));
      break;
    case 'removed-item':
      remove(['C4-01']);
      break;
    case 'immediate-on-removed':
      remove(['C4-02']);
      findings[0]!.severity = 'immediate';
      findings.push({
        id: uuid('f', 4),
        question_id: qid('C4-02'),
        finding_number: 10,
        description: 'ממצא נמוך על סעיף שהוסר — אינו מודפס.',
        location_text: '',
        area_name: null,
        severity: 'low',
        created_by: U.a,
        created_at: t(19),
        withdrawn_at: null,
        withdrawn_reason: null,
      });
      break;
    case 'long-notes':
      data.visit_details = {
        visit_kind: 'critical_stage',
        workers_on_site: 23,
        attendees: 'מנהל העבודה, ממונה הבטיחות, נציג המזמין',
        inspector_notes: Array.from(
          { length: 30 },
          (_, i) =>
            `${i + 1}. הנחיה כללית מספר ${i + 1}: יש לוודא שכל העובדים באתר מודעים לסיכונים ולאמצעי הזהירות הנדרשים לפני תחילת העבודה.`,
        )
          .join('\n')
          .slice(0, 3000),
      };
      break;
    case 'unknown-severity':
      findings[0]!.severity = 'critical' as unknown as StoredSeverity;
      data.previous_findings![0]!.severity = 'urgent' as unknown as StoredSeverity;
      break;
    case 'injection':
      // גם פרק המעקב: טקסט שהבקר הזין בדיווח התיקון מוצג כטקסט, מספר זהות מוסתר.
      data.followups = [
        followupRecord(data.previous_findings![0]!, 'reported_fixed_pending', {
          fix_reported_by_text:
            '<img src=x onerror="window.injected=3"> מנהל העבודה ת.ז. 123456789',
          fix_reported_on: '2026-09-25',
          note: '<script>window.injected=4</script>',
          before: [followupPhoto(90, 'לפני — הדגמה', true)],
          after: [followupPhoto(91, '</figure><b>אחרי</b>', true)],
        }),
      ];
      break;
    case 'followup-pending':
      data.followups = [
        followupRecord(data.previous_findings![0]!, 'reported_fixed_pending', {
          fix_reported_by_text: 'מנהל העבודה, יוסי',
          fix_reported_on: '2026-09-25',
          fix_report_text: 'הותקן מעקה זמני; מעקה קבוע יותקן בשבוע הבא.',
          evidence_source: 'verbal',
          note: 'לא ראיתי בעצמי — הקומה הייתה סגורה.',
          before: [followupPhoto(90, 'המעקה החסר — מהדוח הקודם', true)],
          after: [followupPhoto(91, 'תמונה שנשלחה מהאתר', true)],
        }),
      ];
      break;
    case 'followup-all-verified':
      data.followups = data.previous_findings!.map((pf, i) =>
        followupRecord(pf, 'verified_fixed', {
          evidence_source: 'inspector_observed',
          note: i === 0 ? 'המעקה הותקן ונבדק.' : '',
          before: [followupPhoto(90 + i * 2, 'לפני', true)],
          after: [followupPhoto(91 + i * 2, 'אחרי — תקין', true)],
        }),
      );
      break;
    case 'followup-mixed': {
      for (let i = 3; i <= 5; i += 1)
        data.previous_findings!.push({
          id: uuid('pf', i),
          finding_number: 10 + i,
          description: `ממצא קודם ${i}: ${['סולם פגום בקומה 3', 'כבל חשוף במחסן', 'מטף שפג תוקפו'][i - 3]}`,
          location_text: ['קומה 3', 'מחסן', 'קומת כניסה'][i - 3]!,
          severity: (['high', 'immediate', 'low'] as const)[i - 3]!,
          visit_date: '2026-09-21',
        });
      const [pf1, pf2, pf3, pf4] = data.previous_findings!;
      data.followups = [
        followupRecord(pf1!, 'verified_fixed', {
          evidence_source: 'inspector_observed',
          before: [followupPhoto(90, 'לפני', true)],
          after: [followupPhoto(91, 'אחרי', true)],
        }),
        followupRecord(pf2!, 'verified_fixed', { after: [followupPhoto(92, '', true)] }),
        followupRecord(pf3!, 'verified_not_fixed', {
          note: 'הסולם עדיין בשימוש.',
          after: [followupPhoto(93, 'הסולם — עדיין פגום', true)],
        }),
        followupRecord(pf4!, 'reported_fixed_pending', {
          fix_reported_by_text: 'חשמלאי האתר',
          fix_reported_on: '2026-09-27',
          evidence_source: 'document',
        }),
        // pf5 — בלי רשומה: ״לא נבדק בביקור זה״ (נספר, לא נעלם).
      ];
      break;
    }
    case 'followup-fifty': {
      data.previous_findings = Array.from({ length: 50 }, (_, i) => ({
        id: uuid('pf', 100 + i),
        finding_number: 100 + i,
        description: `ממצא קודם ${i + 1}: גידור חסר בקומה ${(i % 9) + 1}, קטע ${i + 2}.`,
        location_text: `קומה ${(i % 9) + 1}`,
        severity: (['immediate', 'high', 'low'] as const)[i % 3]!,
        visit_date: i < 25 ? '2026-09-07' : '2026-09-14',
      }));
      const states: FollowupState[] = [
        'verified_fixed',
        'reported_fixed_pending',
        'verified_not_fixed',
        'not_checked',
      ];
      data.followups = data.previous_findings
        .filter((_, i) => i % 5 !== 4)
        .map((pf, i) =>
          followupRecord(pf, states[i % 4]!, {
            fix_reported_by_text: i % 4 === 1 ? 'מנהל העבודה' : '',
            fix_reported_on: i % 4 === 1 ? '2026-09-26' : null,
            before: [followupPhoto(200 + i, '', true)],
            after: i % 4 === 0 || i % 4 === 2 ? [followupPhoto(300 + i, '', i % 7 !== 3)] : [],
          }),
        );
      break;
    }
    case 'followup-no-after':
      data.followups = [
        followupRecord(data.previous_findings![0]!, 'verified_fixed', {
          evidence_source: 'inspector_observed',
          note: 'אומת בעין; לא צולם.',
          before: [followupPhoto(90, 'לפני', true)],
          after: [],
        }),
      ];
      break;
    case 'followup-after-unknown-time':
      data.followups = [
        followupRecord(data.previous_findings![1]!, 'reported_fixed_pending', {
          fix_reported_by_text: 'קבלן המשנה',
          evidence_source: 'site_photo',
          before: [followupPhoto(90, '', true)],
          after: [followupPhoto(91, 'תמונה מהגלריה', true, null)],
        }),
      ];
      break;
    case 'full-checklist-0-2': {
      // תחולה כפי שטופס הפתיחה היה קובע: שלב ׳שלד׳ בלבד, אתר מתחת לסף, ביקור ראשון.
      const reasonFor = (code: string): [boolean, string] => {
        if (code.startsWith('C11-'))
          return [false, 'הפרק סונן כי לא נבחר שלב חפירות ודיפון — אפשר להחזיר (כלל מוצר)'];
        if (code.startsWith('C12-'))
          return [false, 'הפרק סונן כי לא נבחר שלב ביסוס ויסודות — אפשר להחזיר (כלל מוצר)'];
        if (code.startsWith('C19-'))
          return [false, 'הפרק סונן כי לא נבחר שלב גמר וחזיתות — אפשר להחזיר (כלל מוצר)'];
        if (code.startsWith('C13-')) return [true, 'הפרק מוצג כי נבחר שלב ׳שלד׳ (כלל מוצר)'];
        if (code === 'C2-05') return [false, 'לא מתקיים: שטח ההיתר 15,000 מ״ר ומעלה'];
        if (code === 'C5-06' || code === 'C5-07')
          return [false, 'לא מתקיים: ביקור שאינו ראשון באתר'];
        return [true, 'חל בכל ביקור'];
      };
      for (const i of data.items) {
        const [applicable, reason] = reasonFor(codeOf(i.question_id));
        i.applicable = applicable;
        i.reason = reason;
      }
      data.profile!.permit_area_sqm = 12000;
      data.stages = [{ stage_period_id: uuid('sp', 1), area_id: uuid('ar', 1), name: 'שלד' }];
      // סימונים בכל פרק פעיל, כדי שכל טבלת פרק תודפס עם תוכן.
      let m = 30;
      for (const c of content.chapters) {
        for (const [j, q] of c.questions.entries()) {
          const id = qid(q.code);
          if (answers.some((a) => a.question_id === id)) continue;
          if (!data.items.find((i) => i.question_id === id)!.applicable) continue;
          m += 1;
          answers.push({
            question_id: id,
            state:
              j % 5 === 2 ? 'not_ok' : j % 7 === 3 ? 'not_relevant' : j % 4 === 1 ? null : 'ok',
            note: j % 5 === 2 ? `ליקוי לדוגמה בסעיף ${q.code} (הדגמה).` : '',
            not_checked_reason: j % 4 === 1 ? 'work_not_done' : null,
            answered_by: j % 2 === 0 ? U.a : U.b,
            answered_at: t(m % 59),
          });
        }
      }
      break;
    }
    case 'full-checklist-1-0': {
      // תחולה כפי שטופס הפתיחה קובע (decideApplicability): שלב ׳שלד׳ בלבד, ואף פרק ״לבחירה״ לא
      // סומן — 11, 12, 19 סוננו לפי שלב, 10, 13, 14 בביקור, 4 ו־15–18 ״לבחירה — לא נבחר״; העובדות —
      // facts10. מה שהדוגמה רושמת על פרק 4 — עובר לפרק 9 (moveChapter4).
      decideItems(facts10(['skeleton']));
      data.profile!.permit_area_sqm = 12000;
      data.stages = [{ stage_period_id: uuid('sp', 1), area_id: uuid('ar', 1), name: 'שלד' }];
      moveChapter4();
      // סימונים בכל פרק שבביקור — באותה תבנית כמו בתרחיש של 0.2.
      let m = 30;
      for (const c of content.chapters) {
        for (const [j, q] of c.questions.entries()) {
          const id = qid(q.code);
          if (answers.some((a) => a.question_id === id)) continue;
          if (!data.items.find((i) => i.question_id === id)!.applicable) continue;
          m += 1;
          answers.push({
            question_id: id,
            state:
              j % 5 === 2 ? 'not_ok' : j % 7 === 3 ? 'not_relevant' : j % 4 === 1 ? null : 'ok',
            note: j % 5 === 2 ? `ליקוי לדוגמה בסעיף ${q.code} (הדגמה).` : '',
            not_checked_reason: j % 4 === 1 ? 'work_not_done' : null,
            answered_by: j % 2 === 0 ? U.a : U.b,
            answered_at: t(m % 59),
          });
        }
      }
      // ביקור על 1.0 נפתח אחרי D28 — schema 2, בלי previous_findings.
      data.schema = 2;
      delete data.previous_findings;
      break;
    }
    case 'visit-not-attached':
    case 'visit-attached': {
      // D28 — schema 2: בלי previous_findings; פרק התיקונים רק כשהבקר צירף.
      data.schema = 2;
      delete data.previous_findings;
      delete data.followups;
      if (scenario === 'visit-attached') data.corrections = sampleCorrectionsData();
      break;
    }
    case 'row-photos': {
      // D39 — שתי תמונות בשורה, השאר בנספח (39-2). C1-01 — 2 · C4-01 — 1 (תמונה 4 של הבסיס) ·
      // C4-02 — ממצא 7 (תמונה 1) ו־2 תמונות של הסעיף: תמונת הממצא קודם, אחת לנספח · C2-02 — 5
      // (3 לנספח) · ממצא 8 הכללי — 5 (3 לנספח) · תמונה כללית — בנספח · שאר השורות — 0.
      data.schema = 2;
      delete data.previous_findings;
      delete data.followups;
      answers.push({
        question_id: qid('C2-02'),
        state: 'ok',
        note: 'הפנקס הכללי נמצא במשרד האתר ומעודכן.',
        not_checked_reason: null,
        answered_by: U.b,
        answered_at: t(21),
      });
      addPhoto(101, null, qid('C1-01'), 'תוכנית הבטיחות על לוח המודעות');
      addPhoto(102, null, qid('C1-01'), '');
      addPhoto(103, null, qid('C4-02'), 'הכניסה הצפונית — מבט מבחוץ');
      addPhoto(104, null, qid('C4-02'), 'שלט זמני בשער הדרומי');
      for (let i = 0; i < 5; i += 1)
        addPhoto(105 + i, null, qid('C2-02'), `הפנקס הכללי — עמוד ${i + 1}`);
      for (const [i, side] of ['ממזרח', 'ממערב', 'מצפון'].entries())
        addPhoto(110 + i, uuid('f', 2), null, `חפירה — מבט ${side}`);
      addPhoto(113, null, null, 'מבט כללי על האתר מהכניסה הראשית');
      break;
    }
    case 'defects-in-rows': {
      // D39 — הליקוי בשורת הסעיף: בסטטוס — ״לא תקין״ / ״דורש תיקון״ עם החומרה הגבוהה; בהערות —
      // הערת הבקר ושורה לכל ממצא (מספר, חומרה, תיאור, מיקום, ״נרשמה התראת סיכון״).
      data.schema = 2;
      delete data.previous_findings;
      delete data.followups;
      const mark = (code: string, state: 'not_ok' | 'needs_fix', note: string) => {
        const existing = answers.find((a) => a.question_id === qid(code));
        if (existing !== undefined) {
          existing.state = state;
          existing.note = note;
          existing.not_checked_reason = null;
          return;
        }
        answers.push({
          question_id: qid(code),
          state,
          note,
          not_checked_reason: null,
          answered_by: U.a,
          answered_at: t(30),
        });
      };
      const finding = (
        n: number,
        code: string | null,
        severity: StoredSeverity | null,
        description: string,
        location = '',
        area: string | null = null,
      ) =>
        findings.push({
          id: uuid('f', n),
          question_id: code === null ? null : qid(code),
          finding_number: n,
          description,
          location_text: location,
          area_name: area,
          severity,
          created_by: n % 2 === 0 ? U.a : U.b,
          created_at: t(20 + n),
          withdrawn_at: null,
          withdrawn_reason: null,
        });
      // ״דורש תיקון״ (תוכנית הבטיחות) עם ממצא ״גבוה״.
      mark('C1-01', 'needs_fix', 'התוכנית שבאתר היא מהדורה 2; המהדורה המעודכנת לא הוצגה.');
      finding(
        10,
        'C1-01',
        'high',
        'תוכנית הבטיחות שבאתר אינה המהדורה המעודכנת — יש להציג את המהדורה האחרונה ולעדכן את העובדים.',
        'משרד האתר',
        'מגדל A',
      );
      // ״מיידי״ עם התראת סיכון ותמונה — בשורת הסעיף.
      mark('C5-02', 'not_ok', 'פתח פתוח בקומה 3.');
      finding(
        11,
        'C5-02',
        'immediate',
        'פתח ברצפה בקומה 3 ללא כיסוי וללא מעקה, ליד גרם המדרגות — סכנת נפילה.',
        'קומה 3, ליד גרם המדרגות',
        'מגדל A',
      );
      addPhoto(120, uuid('f', 11), qid('C5-02'), 'הפתח בקומה 3');
      data.alerts.push({
        id: uuid('a', 2),
        finding_id: uuid('f', 11),
        description: 'פתח ברצפה ללא כיסוי — סכנת נפילה מיידית.',
        location_text: 'קומה 3',
        created_by: U.a,
        created_at: t(32),
      });
      // ״נמוך״ עם תיאור ארוך (כמה פסקאות) — ההערה אינה נחתכת בשורה.
      mark('C3-02', 'not_ok', '');
      finding(
        12,
        'C3-02',
        'low',
        [
          'בקומת הקרקע ובקומה 1 עבדו שישה עובדים בנעלי ספורט במקום נעלי בטיחות: שניים בקשירת ברזל, שניים בהעברת בלוקים ושניים בניקוי השטח לקראת היציקה.',
          'לדברי מנהל העבודה, נעלי בטיחות במידות המתאימות הוזמנו ויגיעו במהלך השבוע. עד שיגיעו, העובדים הועברו לעבודות שאין בהן הרמה או העברה של חומרים כבדים, והנושא נרשם בפנקס הכללי.',
          'יש לוודא שכל עובד שנכנס לאתר נועל נעלי בטיחות תקינות, לבדוק את מצב הנעליים אחת לשבוע ולרשום את חלוקת הציוד לכל עובד בשמו. עובד בלי נעלי בטיחות אינו מתחיל לעבוד.',
          'הבקר יבדוק את הנושא שוב בביקור הבא, כולל רישום החלוקה ובדיקה מדגמית של העובדים בכל הקומות הפעילות.',
        ].join('\n\n'),
      );
      // שני ממצאים באותה שורה (״נמוך״ ו״גבוה״) — בצ׳יפ החומרה הגבוהה.
      mark('C4-04', 'not_ok', 'המעבר הראשי חסום חלקית.');
      finding(13, 'C4-04', 'low', 'פסולת בניין במעבר הראשי.', 'מעבר ראשי');
      finding(
        14,
        'C4-04',
        'high',
        'משאבת בטון חוסמת את דרך המילוט מהחניון.',
        'דרך המילוט',
        'חניון B',
      );
      // חומרה שלא צוינה — ״לא תקין״ בלי תוספת, ״לא צוינה״ בשורת הממצא.
      mark('C5-03', 'not_ok', '');
      finding(15, 'C5-03', null, 'מעקה חסר בגרם המדרגות בין קומה 2 לקומה 3.', 'גרם מדרגות מזרחי');
      // ״לא תקין״ בלי ממצא (D23) — ההערה היא הליקוי.
      mark('C3-03', 'not_ok', 'שלושה עובדים ללא אפוד זוהר באזור תנועת המשאיות.');
      // ממצא כללי שני (לצד ממצא 8 של הבסיס, ״מיידי״ עם התראה) — בלי מיקום ובלי תמונה.
      finding(16, null, 'high', 'חומרים דליקים מאוחסנים ליד לוח החשמל הזמני.');
      break;
    }
    case 'table-1-0': {
      // D39 על תוכן 1.0 — הדוח בסגנון טבלה, כמו בדוגמה: תוכן שאושר (D37 ס׳ 3: ״אושר לשימוש בידי״,
      // בלי הערת האימות ובלי תוויות) באתר שאינו הדגמה (בלי הערת ההדגמה); שלבי שלד וגמר — 10, 13, 14,
      // 19 בביקור, 11 ו־12 סוננו לפי שלב, וחמשת הפרקים ״לבחירה״ לא נבחרו: 7 פרקים לא נכללו (D38 ס׳ 4).
      // ממצאים ותמונות — רק בפרקי ״תמיד״ (פרק 4 ״לבחירה״!), עד 2 בשורה — ולכן אין נספח.
      data.schema = 2;
      delete data.previous_findings;
      delete data.followups;
      data.site.is_demo = false;
      decideItems(facts10(['skeleton', 'finishing']));
      data.profile!.permit_area_sqm = 12000;
      data.stages = [
        { stage_period_id: uuid('sp', 1), area_id: uuid('ar', 1), name: 'שלד' },
        { stage_period_id: uuid('sp', 3), area_id: uuid('ar', 2), name: 'גמר' },
      ];
      data.visit_details = {
        visit_kind: 'periodic',
        workers_on_site: 38,
        attendees: 'מנהל העבודה, נציג המזמין, ממונה הבטיחות',
      };
      moveChapter4();
      // מהתשובות של הדוגמה נשארות הגדר והשילוט (בפרק 9); האחרות נכתבו לנוסח של 0.1. כל סעיף שבביקור
      // מסומן — ״תקין״ בלי הערה, אלא אם נקבע כאן אחרת.
      type Mark = [state: AnswerValue | null, note: string, reason?: NotCheckedReason];
      const marks: Record<string, Mark> = {
        'C1-01': [
          'ok',
          'תוכנית ניהול הבטיחות (מהדורה 2א) נמצאת במשרד האתר ומעודכנת לשלבי השלד והגמר.',
        ],
        'C2-03': ['ok', 'מנהל העבודה נוכח באתר ומשגיח על העבודות.'],
        'C5-01': ['ok', 'הפנקס הכללי במשרד האתר; המינויים וההודעות מצורפים אליו.'],
        'C6-01': ['ok', 'ההדרכה האחרונה התקיימה בתחילת החודש; טופסי החתימה בתיק ההדרכות.'],
        'C7-01': ['ok', 'העובדים בקומה 7 חובשים קסדות ומשתמשים ברתמות.'],
        'C8-03': ['not_ok', 'ערימת בלוקים על שפת התקרה בקומה 7.'],
        'C8-05': ['ok', 'מתקני הרווחה נקיים; מי שתייה זמינים בכל קומה פעילה.'],
        'C9-04': ['not_relevant', 'לא היו כניסות ויציאות של משאיות בזמן הביקור.'],
        'C9-08': ['ok', 'השלט בכניסה מעודכן, ובו פרטי המזמין, הבקר ומנהל העבודה.'],
        'C10-04': ['not_ok', 'פתח ברצפה בקומה 5 ללא כיסוי; כוסה במהלך הביקור.'],
        'C10-07': ['ok', 'סולם הגישה לפיגום קשור, יציב ובולט כמטר מעל משטח היציאה.'],
        'C13-07': [null, '', 'work_not_done'],
        'C14-02': ['ok', 'הפיגום נבדק בתחילת השבוע והבדיקה רשומה בפנקס.'],
        'C19-05': [null, 'טרם החלו עבודות הזכוכית בחזית.', 'work_not_done'],
        'C20-03': ['not_ok', 'מטף בקומת הקרקע ללא תווית בדיקה בתוקף.'],
        'C20-04': ['ok', 'ערכת עזרה ראשונה מלאה במשרד האתר.'],
      };
      const kept = new Set([qid('C9-01'), qid('C9-02')]);
      data.answers = answers.filter((a) => kept.has(a.question_id));
      let m = 30;
      for (const q of questions) {
        if (kept.has(q.id) || !data.items.find((i) => i.question_id === q.id)!.applicable) continue;
        const [state, note, reason]: Mark = marks[q.code] ?? ['ok', ''];
        m += 1;
        data.answers.push({
          question_id: q.id,
          state,
          note,
          not_checked_reason: reason ?? null,
          answered_by: m % 2 === 0 ? U.a : U.b,
          answered_at: t(m % 59),
        });
      }
      // ״גבוה״ ו״מיידי״ (עם התראת סיכון) בשורות הסעיפים, כל אחד עם תמונה; ממצא 7 (שילוט, ״נמוך״) וממצא
      // 8 (כללי) — של הדוגמה. ״לא תקין״ בלי ממצא (D23) — C10-04.
      const finding = (n: number, code: string, severity: StoredSeverity, description: string) =>
        findings.push({
          id: uuid('f', n),
          question_id: qid(code),
          finding_number: n,
          description,
          location_text: n === 10 ? 'קומת הקרקע, ליד חדר המדרגות' : 'קומה 7, צד צפון',
          area_name: 'מגדל A',
          severity,
          created_by: U.a,
          created_at: t(30 + n),
          withdrawn_at: null,
          withdrawn_reason: null,
        });
      finding(
        10,
        'C20-03',
        'high',
        'מטף הכיבוי בקומת הקרקע ללא תווית בדיקה בתוקף — יש לבדוק אותו או להחליפו.',
      );
      finding(
        11,
        'C8-03',
        'immediate',
        'ערימת בלוקים מאוחסנת על שפת התקרה בקומה 7, ללא גידור — סכנת נפילת חפצים אל המעבר שמתחת.',
      );
      data.alerts.push({
        id: uuid('a', 2),
        finding_id: uuid('f', 11),
        description: 'ערימת בלוקים על שפת התקרה — סכנת נפילת חפצים.',
        location_text: 'קומה 7',
        created_by: U.a,
        created_at: t(42),
      });
      addPhoto(130, null, qid('C7-01'), 'עובדים בקומה 7 — קסדות ורתמות');
      addPhoto(131, uuid('f', 11), qid('C8-03'), 'ערימת הבלוקים על שפת התקרה');
      addPhoto(132, null, qid('C8-03'), 'שפת התקרה — מבט מהקומה שמתחת');
      addPhoto(133, null, qid('C9-08'), 'השלט בכניסה הראשית');
      addPhoto(134, uuid('f', 10), qid('C20-03'), 'המטף בקומת הקרקע');
      addPhoto(135, null, qid('C20-04'), 'ערכת העזרה הראשונה');
      break;
    }
    case 'excluded-many': {
      // D38 ס׳ 4 — 11 פרקים שלא נכללו: תוכן 1.0; נבחר שלב מסוג ׳אחר׳, שאינו מתאים לאף אחד מששת
      // הפרקים ״לפי שלב״ (10–14, 19), וחמשת הפרקים ״לבחירה״ לא נבחרו. בדוח — שורה אחת בראש הצ׳קליסט,
      // ובתקציר שורה לכל אחד מתשעת הפרקים שבביקור (במספר הפרק עצמו) ושורה אחת ״לא נכללו: 11 פרקים״.
      data.schema = 2;
      delete data.previous_findings;
      delete data.followups;
      decideItems(facts10(['other']));
      data.profile!.permit_area_sqm = 12000;
      data.stages = [
        { stage_period_id: uuid('sp', 4), area_id: uuid('ar', 1), name: 'עבודות פיתוח' },
      ];
      moveChapter4();
      break;
    }
    case 'legacy-0-2': {
      // snapshot ישן — ביקור על טיוטת התוכן 0.2 (הדגמה) מאחרי D28 ומלפני D37–D39 (schema 2): מודפס כמו
      // היום — הערת ההדגמה, ״מצב אימות הסעיפים״, ״נוסח אומת״ בשורות ו״תוכן הדגמה״ במקורות, בלי ״אושר
      // לשימוש בידי״. מה שלא נכלל מהסיבות של אז — 11, 12, 19 סוננו לפי שלב (כלל מוצר) ופרק 15 הוסר
      // בידי הבקר (D21) — בשורת ״לא נכללו״ אחת (D38 ס׳ 4); C2-05 סונן לפי הפרופיל — בקבוצה, כמו היום.
      data.schema = 2;
      delete data.previous_findings;
      delete data.followups;
      const stageOf = (code: string) =>
        code.startsWith('C11-')
          ? 'חפירות ודיפון'
          : code.startsWith('C12-')
            ? 'ביסוס ויסודות'
            : code.startsWith('C19-')
              ? 'גמר וחזיתות'
              : null;
      for (const i of data.items) {
        const code = codeOf(i.question_id);
        const filtered = stageOf(code);
        i.applicable = filtered === null && code !== 'C2-05';
        i.reason =
          filtered !== null
            ? `הפרק סונן כי לא נבחר שלב ${filtered} — אפשר להחזיר (כלל מוצר)`
            : code === 'C2-05'
              ? 'לא מתקיים: שטח ההיתר 15,000 מ״ר ומעלה'
              : code.startsWith('C13-')
                ? 'הפרק מוצג כי נבחר שלב ׳שלד׳ (כלל מוצר)'
                : 'חל בכל ביקור';
      }
      remove(questions.filter((q) => q.code.startsWith('C15-')).map((q) => q.code));
      data.profile!.permit_area_sqm = 12000;
      data.stages = [{ stage_period_id: uuid('sp', 1), area_id: uuid('ar', 1), name: 'שלד' }];
      break;
    }
    default:
      break;
  }
  return data;
}

/** תמונת לפני/אחרי סינתטית (D25). `observedAt` null — ״מועד לא ידוע״ (RQ-G06). */
function followupPhoto(
  n: number,
  caption: string,
  stored: boolean,
  observedAt: string | null = new Date(Date.UTC(2026, 8, 20 + (n % 8), 7, n % 60)).toISOString(),
): FollowupPhoto {
  return {
    id: uuid('fe', n),
    storage_path: `site/${uuid('fe', n)}/followup-${n}.jpg`,
    mime_type: 'image/jpeg',
    caption,
    observed_at: observedAt,
    observed_at_source: observedAt === null ? 'unknown' : 'device',
    stored,
  };
}

/** רשומת מעקב סינתטית לממצא קודם; השדות שלא נמסרו — ריקים (הכול רשות, 6ב-2). */
function followupRecord(
  pf: NonNullable<VisitReportData['previous_findings']>[number],
  state: FollowupState,
  extra: Partial<
    Pick<
      FollowupReportItem,
      | 'fix_reported_by_text'
      | 'fix_reported_on'
      | 'fix_report_text'
      | 'evidence_source'
      | 'note'
      | 'not_checked_reason'
      | 'before'
      | 'after'
    >
  > = {},
): FollowupReportItem {
  return {
    finding_id: pf.id,
    finding_number: pf.finding_number,
    description: pf.description,
    location_text: pf.location_text,
    area_name: null,
    severity: pf.severity,
    question_code: 'C4-02',
    source: {
      visit_id: uuid('pv', pf.finding_number),
      visit_date: pf.visit_date,
      report_number: `V-${pf.visit_date.replace(/-/g, '')}-${pf.id.replace(/-/g, '').slice(0, 6).toUpperCase()}`,
      report_approved: true,
    },
    state,
    not_checked_reason: extra.not_checked_reason ?? null,
    fix_reported_by_text: extra.fix_reported_by_text ?? '',
    fix_reported_on: extra.fix_reported_on ?? null,
    fix_report_text: extra.fix_report_text ?? '',
    evidence_source: extra.evidence_source ?? 'none',
    note: extra.note ?? '',
    recorded_by: U.a,
    recorded_at: '2026-09-28T07:40:00.000Z',
    before: extra.before ?? [],
    after: extra.after ?? [],
  };
}

export function sampleEnvelope(mode: 'draft' | 'approved'): ReportEnvelope {
  return {
    reportNumber: 'V-20260928-111111',
    version: 1,
    reviewedAt: '2026-09-28T10:00:00.000Z',
    reviewedByName: 'בקר א (הדגמה)',
    approval:
      mode === 'draft'
        ? null
        : {
            approvedAt: '2026-09-28T10:12:00.000Z',
            approvedByName: 'בקר ב (הדגמה)',
            participants: [
              {
                user_id: U.a,
                name: 'בקר א (הדגמה)',
                acts: ['opened', 'answered', 'finding', 'photo', 'submitted', 'reviewed'],
              },
              {
                user_id: U.b,
                name: 'בקר ב (הדגמה)',
                acts: ['answered', 'finding', 'photo', 'approved'],
              },
            ],
          },
  };
}
